import React, { useState } from 'react';
import { ApiError } from '../types/ApiError';
import styled from 'styled-components';
import { Formik } from 'formik';
import * as Yup from 'yup';
import {
  Modal,
  ModalContent,
  ModalFooter,
  Button,
  H3,
  InputText,
  Grid,
  Caption,
  CellText,
} from '@increasecard/typed-components';
import { createInvitation, verifyEmail } from '../services/InvitationService';
import { ApiErrorMessage } from './ApiErrorMessage';
import { Trans, useTranslation } from 'react-i18next';
import { useUserSettings } from '../hooks/useUserSettings';

const RolesContainer = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 16px;
  flex-wrap: wrap;
`;

const RoleCard = styled.div<{ selected: boolean }>`
  flex: 1;
  min-width: 200px;
  padding: 16px;
  border: 1px solid
    ${({ selected }) =>
      selected ? 'var(--color-increase-light30)' : 'var(--color-gray-grayMedium)'};
  border-radius: 8px;
  cursor: pointer;
  background-color: ${({ selected }) => (selected ? 'var(--color-increase-light)' : 'white')};
  transition: all 0.2s ease-in-out;

  &:hover {
    border-color: var(--color-increase-regular);
  }

  ${Caption} {
    color: var(--color-gray-blackLight);
    font-size: 12px;
  }

  ${CellText} {
    font-weight: bold;
    margin-bottom: 4px;
    display: block;
    color: ${({ selected }) =>
      selected ? 'var(--color-gray-blackLight)' : 'var(--color-gray-blackLight)'};
  }
`;

const AVAILABLE_PAY_ROLES = {
  // pay_agregador_admin: 'pay_agregador_admin',
  pay_agregador_viewer: 'pay_agregador_viewer',
  pay_agregador_operator: 'pay_agregador_operator',
};

const validationSchema = Yup.object().shape({
  firstName: Yup.string().required('Campo obligatorio'),
  lastName: Yup.string().required('Campo obligatorio'),
  email: Yup.string()
    .test('valid-email', 'Email incorrecto', (value) => !value || verifyEmail(value))
    .required('Campo obligatorio'),
});

export type PayRole = keyof typeof AVAILABLE_PAY_ROLES;

export interface InviteUserModalProps {
  visible: boolean;
  onClose: () => void;
  onSuccess: (email: string) => void;
  initialData?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    authorizations: { product_name: 'pay'; role: string }[];
  };
}

export default function InviteUserModal({
  visible,
  onClose,
  onSuccess,
  initialData,
}: InviteUserModalProps) {
  const { t } = useTranslation('settingsUsers');
  const { updateUserSettings } = useUserSettings(initialData?.id);
  const [error, setError] = useState<ApiError | null>(null);

  React.useEffect(() => {
    if (visible) {
      setError(null);
    }
  }, [visible]);

  const initialValues = {
    firstName: initialData?.firstName || '',
    lastName: initialData?.lastName || '',
    email: initialData?.email || '',
    role:
      initialData?.authorizations.find((a) => a.product_name === 'pay')?.role ||
      AVAILABLE_PAY_ROLES.pay_agregador_viewer,
  };

  return (
    <Modal visible={visible} onClose={onClose} width="600">
      <Formik
        enableReinitialize={true}
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={async (values, { setSubmitting }) => {
          setError(null);
          try {
            if (initialData) {
              const authorizations = initialData.authorizations
                ? [...initialData.authorizations]
                : [];
              const payAuthIndex = authorizations.findIndex((a) => a.product_name === 'pay');
              if (payAuthIndex !== -1) {
                authorizations[payAuthIndex] = {
                  ...authorizations[payAuthIndex],
                  role: values.role,
                };
              } else {
                authorizations.push({ product_name: 'pay', role: values.role });
              }

              await updateUserSettings(initialData.id, {
                first_name: values.firstName,
                last_name: values.lastName,
                email: values.email,
                authorizations,
              });
            } else {
              await createInvitation({
                first_name: values.firstName,
                last_name: values.lastName,
                email: values.email,
                role: values.role,
              });
            }
            onSuccess(values.email);
            onClose();
          } catch (err) {
            setError(err as ApiError);
          } finally {
            setSubmitting(false);
          }
        }}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          handleSubmit,
          isSubmitting,
          setFieldValue,
          isValid,
        }) => (
          <>
            <ModalContent>
              <H3 style={{ marginBottom: 20 }}>
                {initialData ? 'Editar usuario' : 'Invitar usuario'}
              </H3>
              <Grid rowGap={2}>
                <InputText
                  className="grid-span-desktop-6"
                  id="firstName"
                  label="Nombre"
                  value={values.firstName}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  required
                  errorMessage={touched.firstName && errors.firstName}
                />
                <InputText
                  className="grid-span-desktop-6"
                  id="lastName"
                  label="Apellido"
                  value={values.lastName}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  required
                  errorMessage={touched.lastName && errors.lastName}
                />
                <InputText
                  className="grid-span-desktop-12"
                  disabled={!!initialData}
                  id="email"
                  label="Email"
                  type="email"
                  value={values.email}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  required
                  errorMessage={touched.email && errors.email}
                />
              </Grid>

              <CellText weight="bold" style={{ marginTop: 20, display: 'block' }}>
                Seleccioná un rol
              </CellText>
              <RolesContainer>
                {Object.values(AVAILABLE_PAY_ROLES).map((role) => (
                  <RoleCard
                    key={role}
                    selected={values.role === role}
                    onClick={() => setFieldValue('role', role)}
                  >
                    <CellText>
                      <Trans i18nKey={t(`roles.${role}`)} />
                    </CellText>
                    <Caption>
                      <Trans i18nKey={t(`roles.${role}_description`)} />
                    </Caption>
                  </RoleCard>
                ))}
              </RolesContainer>

              <ApiErrorMessage error={error} marginTop={20} />
            </ModalContent>
            <ModalFooter align="right">
              <Button buttonType="invisible" onClick={onClose} disabled={isSubmitting}>
                Cancelar
              </Button>
              <Button
                buttonType="primary"
                onClick={handleSubmit}
                disabled={isSubmitting || !isValid}
              >
                {isSubmitting ? 'Enviando...' : initialData ? 'Guardar' : 'Enviar'}
              </Button>
            </ModalFooter>
          </>
        )}
      </Formik>
    </Modal>
  );
}
